import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/client';
import { Category, BranchProduct, Product, CategoryResponse, PaginatedResponse, ProductReview, ProductCreateInput, BranchProductAssignInput, ProductData } from '../types';
import { useCart } from './CartContext';
import { useShopContext } from './ShopContext';
import { useAnalytics } from './AnalyticsContext';

interface ProductContextType {
  categories: Category[];
  products: BranchProduct[];
  featuredProducts: BranchProduct[];
  selectedProduct: BranchProduct | null;
  loading: boolean;
  fetchNextProducts: () => Promise<void>;

  error: string | null;
  next: string | null;
  previous: string | null;
  fetchCategories: () => Promise<void>;
fetchProducts: (searchParams?: URLSearchParams, append?: boolean) => Promise<void>;
  fetchFeaturedProducts: (branchId: number) => Promise<void>;
  fetchProduct: (productId: number, branchId?: number) => Promise<void>;
  fetchAllProducts: () => Promise<void>;
  createProduct: (productData: FormData) => Promise<Product>;
  assignProductToBranch: (data: BranchProductAssignInput) => Promise<BranchProduct>;
  addToCart: (productId: number, colorId?: number) => void;
  submitReview: (productId: number, review: { rating: number; comment: string }) => Promise<void>;
  reviews: ProductReview[];
  fetchProductReviews: (productId: number) => Promise<void>;
  setSelectedProductMe: (product:BranchProduct) => Promise<BranchProduct>;
  updateProduct: (productId: number, data: FormData) => Promise<ProductData>;
  addImages: (productId: number, files: File[]) => Promise<void>;

    deleteBranchProduct: (branchProductId: number) => Promise<void>;

}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<BranchProduct[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<BranchProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<BranchProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const { addToCart: cartAddToCart } = useCart();
  const [next, setNext] = useState<string | null>(null);
const [previous, setPrevious] = useState<string | null>(null);
const [count, setCount] = useState<number>(0);
  const { trackEvent } = useAnalytics();

  const setSelectedProductMe = async (product: BranchProduct): Promise<BranchProduct> => {
    setSelectedProduct(product);
    return product;
  };

  const { selectedBranch, adminBranches } = useShopContext();

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get<PaginatedResponse<CategoryResponse>>('/products/categories/');
      setCategories(response.data.results || []);
    } catch (err) {
      setError('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

// في ProductContext
const fetchProducts = async (searchParams?: URLSearchParams, append: boolean = false) => {
  setLoading(true);
  setError(null);
  try {
    const params = new URLSearchParams(searchParams);
    const response = await api.get<PaginatedResponse<BranchProduct>>('/branch-products/', {
      params,
    });

    if (append) {
      setProducts((prev) => [...prev, ...(response.data.results || [])]);
    } else {
      setProducts(response.data.results || []);
    }

    setNext(response.data.next);
    setPrevious(response.data.previous);
    setCount(response.data.count);
  } catch (err: any) {
    const errorMessage = err.response?.status === 404
      ? 'No products found for the given filters'
      : err.response?.data?.detail || 'Failed to fetch products';
    setError(errorMessage);
    console.error('Failed to fetch products', err);
  } finally {
    setLoading(false);
  }
};



  const fetchFeaturedProducts = async (branchId: number) => {
    try {
      setLoading(true);
      const response = await api.get<PaginatedResponse<BranchProduct>>('/branch-products/', {
        params: { branch: branchId, featured: true },
      });
      setFeaturedProducts(response.data.results || []);
    } catch (err) {
      setError('Failed to fetch featured products');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductReviews = async (productId: number) => {
    try {
      setLoading(true);
      const response = await api.get<ProductReview[]>(`/products/${productId}/reviews/`);
      setReviews(response.data);
    } catch (err) {
      setLoading(false)
      // setError('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async (productId: number, review: { rating: number; comment: string }) => {
    try {
      setLoading(true);
      await api.post(`/products/${productId}/add_review/`, review);
      await fetchProductReviews(productId);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit review');
      console.error('Submit review error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProduct = async (productId: number, branchId?: number) => {
    try {
      setLoading(true);
      const response = await api.get<PaginatedResponse<BranchProduct>>('/branch-products/', {
        params: {  product: productId },
      });
      setSelectedProduct(response.data.results[0] || null);
    } catch (err) {
      setError('Failed to fetch product');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get<PaginatedResponse<Product>>('/products/');
      setAllProducts(response.data.results || []);
    } catch (err) {
      setError('Failed to fetch all products');
      console.error('Failed to fetch all products', err);
    } finally {
      setLoading(false);
    }
  };

  const createProduct = async (productData: FormData): Promise<Product> => {
    try {
      setLoading(true);
      const response = await api.post<Product>('/products/', productData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchAllProducts();
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.sku
        ? 'SKU already exists'
        : err.response?.data?.images
        ? 'Invalid image file'
        : err.response?.data?.detail || 'Failed to create product';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const assignProductToBranch = async (data: BranchProductAssignInput): Promise<BranchProduct> => {
    try {
      setLoading(true);
      const response = await api.post<BranchProduct>('/branch-products/', data);
      if (data.branch_id) {
        await fetchProducts(new URLSearchParams({ branch: data.branch_id.toString() }));
      }
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to assign product to branch');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (productId: number, colorId?: number) => {
    // console.log(productId, products);
    const product = products.find((p) => p.product.id === productId || p.id === productId);
    if (product) {
      cartAddToCart(product, colorId);
    } else {
      setError('Product not found');
    }
  };

  const deleteBranchProduct = async (branchProductId: number) => {
    try {
      setLoading(true);
      await api.delete(`/branch-products/${branchProductId}/`);
      await fetchProducts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete product');
      throw err;
    } finally {
      setLoading(false);
    }
  };    const updateProduct = async (productId: number, data: FormData) => {
    try {
      setLoading(true);
      const response = await api.patch<ProductData>(`/products/${productId}/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchProducts();
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update product');
      throw err;
    } finally {
      setLoading(false);
    }
  };
   const addImages = async (productId: number, files: File[]) => {
    try {
      setLoading(true);
      const formData = new FormData();
      files.forEach((file) => formData.append('images', file));
      await api.post(`/products/${productId}/add-images/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchProducts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add images');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);
  // 🔹 Listener مركزي على مشاهدة المنتجات
  useEffect(() => {
    if (selectedProduct) {
      trackEvent("view_product", {
        product_id: selectedProduct.product.id,
        branch_id: selectedProduct.branch.id,
        name: selectedProduct.product.name,
      });
    }
  }, [selectedProduct, ]);

  // 🔹 Listener مركزي على تغييرات الكارت

const fetchNextProducts = async () => {
  if (!next || loading) return;

  setLoading(true);
  setError(null);

  try {
    // next جاي URL كامل من Django
    const response = await api.get<PaginatedResponse<BranchProduct>>(next);

    setProducts((prev) => [
      ...prev,
      ...(response.data.results || []),
    ]);

    setNext(response.data.next);
    setPrevious(response.data.previous);
    setCount(response.data.count);
  } catch (err: any) {
    setError(
      err.response?.data?.detail || 'Failed to fetch next products'
    );
    console.error(err);
  } finally {
    setLoading(false);
  }
};

  // 🔹 Listener مركزي على إضافة review جديد

  return (
    <ProductContext.Provider
      value={{
        categories,
        products,
        featuredProducts,
        selectedProduct,
        loading,
        error,
        setSelectedProductMe,
        fetchCategories,
        fetchProducts,
        fetchFeaturedProducts,
        fetchProduct,
        fetchAllProducts,
        createProduct,
        assignProductToBranch,
        addToCart,
        reviews,
        fetchProductReviews,
        submitReview,
                deleteBranchProduct,
        updateProduct,
                addImages,
                    fetchNextProducts, // 👈 هنا

    next,
    previous,

      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProductContext = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProductContext must be used within a ProductProvider');
  }
  return context;
};